import {
  Confetti,
  GameController,
  Horse,
  type Icon as PhosphorIcon,
  PokerChip,
  SoccerBall,
  Spade,
  Ticket,
} from "@phosphor-icons/react";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {useAuthStore} from "../../../store/useAuth.store";

interface NavItem {
  key: string;
  label: string;
  Icon: PhosphorIcon;
  route: string;
  requireAuth: boolean;
}

interface ButtonsNavbarProps {
  sidebarCollapsed?: boolean;
}

const navCls =
  "flex flex-row items-center justify-center gap-1.5 px-2 max-[1450px]:gap-1 max-[1199px]:overflow-x-auto max-[1199px]:scrollbar-none";

const pillBaseCls = [
  "group relative inline-flex items-center justify-center gap-2",
  "h-10 px-4 rounded-full",
  "text-(length:--font-body) font-semibold font-[var(--font-family)] tracking-tight",
  "text-white/85",
  "bg-[rgba(255,255,255,0.03)]",
  "border border-[var(--color-border-subtle)]",
  "backdrop-blur-md",
  "transition-[transform,background,border-color,box-shadow,color] duration-200 ease-out",
  "hover:text-white hover:border-[rgba(215,50,168,0.45)]",
  "hover:bg-[linear-gradient(135deg,rgba(250,84,0,0.16),rgba(215,50,168,0.18))]",
  "hover:shadow-[0_8px_24px_-10px_rgba(215,50,168,0.55),inset_0_1px_0_rgba(255,255,255,0.06)]",
  "hover:-translate-y-px",
  "active:translate-y-0 active:scale-[0.98]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-0",
  "max-[1450px]:h-9 max-[1450px]:px-3 max-[1450px]:text-(length:--font-caption)",
  "max-[1280px]:px-2.5 max-[1280px]:gap-1.5",
].join(" ");

const pillActiveCls = [
  "text-white",
  "bg-[linear-gradient(135deg,#fa5400_0%,#d732a8_100%)]",
  "border-transparent",
  "shadow-[0_10px_28px_-10px_rgba(215,50,168,0.65),inset_0_1px_0_rgba(255,255,255,0.25)]",
  "hover:text-white",
  "hover:bg-[linear-gradient(135deg,#fa5400_0%,#d732a8_100%)]",
  "hover:border-transparent",
  "hover:shadow-[0_12px_32px_-10px_rgba(215,50,168,0.75),inset_0_1px_0_rgba(255,255,255,0.25)]",
].join(" ");

const iconCls = "shrink-0 transition-transform duration-200 group-hover:scale-110";
const labelCls = "whitespace-nowrap max-[1100px]:hidden";

const ButtonsNavbar = ({sidebarCollapsed: _sidebarCollapsed = false}: ButtonsNavbarProps) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const items: NavItem[] = useMemo(
    () => [
      {key: "games", label: t("games"), Icon: PokerChip, route: "/gameslist/all", requireAuth: false},
      {key: "casino", label: t("casinoEnVivo", "Casino en vivo"), Icon: Spade, route: "/casino", requireAuth: true},
      {key: "lottery", label: t("lottery"), Icon: Ticket, route: "/lottery", requireAuth: false},
      {key: "bingo", label: t("bingo"), Icon: Confetti, route: "/bingo", requireAuth: true},
      {key: "horses", label: t("horses"), Icon: Horse, route: "/horses", requireAuth: true},
      {key: "sports", label: t("sports"), Icon: SoccerBall, route: "/sports", requireAuth: true},
      {
        key: "esports",
        label: t("e_sports"),
        Icon: GameController,
        route: "/games/pragmatic/vplobby",
        requireAuth: true,
      },
    ],
    [t]
  );

  const handleClick = useCallback(
    (route: string, requireAuth: boolean) => {
      if (requireAuth && authStatus !== "authenticated") {
        handleShowAuthModal();
        return;
      }
      navigate(route);
    },
    [authStatus, handleShowAuthModal, navigate]
  );

  return (
    <nav
      className={navCls}
      aria-label="Categorías de juegos"
    >
      {items.map(({key, label, Icon, route, requireAuth}) => {
        const isActive = location.pathname === route || location.pathname.startsWith(`${route}/`);
        return (
          <button
            key={key}
            type="button"
            onClick={() => handleClick(route, requireAuth)}
            aria-current={isActive ? "page" : undefined}
            aria-label={label}
            className={`${pillBaseCls} ${isActive ? pillActiveCls : ""}`}
          >
            <Icon
              size={18}
              weight="fill"
              aria-hidden="true"
              className={iconCls}
            />
            <span className={labelCls}>{label}</span>
            {key === "casino" && (
              <span
                aria-hidden="true"
                className="shrink-0 w-2 h-2 rounded-full bg-red-500 shadow-[0_0_6px_2px_rgba(239,68,68,0.7)] animate-pulse"
              />
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default ButtonsNavbar;
